import Axios from "../utils/axiosInterceptor";
import { PATIENT_APIS } from "../utils/constants";
import { StatusCodes } from "http-status-codes";
import {
  getApiErrorMessage,
  getDischargeSubmitErrorMessage,
} from "../utils/apiErrorUtils";

export type BpaDocumentDto = {
  id: number;
  documentTypeName: string;
  documentName?: string;
  contentType?: string;
  documentUrl?: string;
  viewUrl?: string;
  deficiencyCheckStatus?: boolean | null;
  remarks?: string | null;
};

export type BpaStageStatus = {
  status: string;
  statusKey?: string;
  stateId?: number | null;
  completed?: boolean;
};

export type DischargeResponse = {
  id?: number;
  visitId: number;
  caseId?: number;
  dateOfDischarge?: string;
  totalBillAmount?: string;
  saveAsDraft?: boolean;
  initiated?: boolean;
  submitted?: boolean;
  approved?: boolean;
  remarks?: string;
  workflowStatus?: string;
  stageStatus?: BpaStageStatus;
  documents?: BpaDocumentDto[];
};

export type BpaDocumentMeta = {
  documentType: string;
  fileName?: string;
};

export type StagedFile = {
  documentType: string;
  file: File;
};

export type SaveDischargeRequest = {
  dateOfDischarge?: string;
  totalBillAmount?: string;
  remarks?: string;
  saveAsDraft?: boolean;
  documents?: BpaDocumentMeta[];
};

export const buildStageFileUploadName = (
  documentType: string,
  file: File,
): string => `${documentType}::${file.name}`;

export const buildSaveFormData = (
  data: Record<string, unknown>,
  files: StagedFile[],
): FormData => {
  const documents = files.map((f) => ({
    documentType: f.documentType,
    fileName: buildStageFileUploadName(f.documentType, f.file),
  }));
  const formData = new FormData();
  formData.append("data", JSON.stringify({ ...data, documents }));
  files.forEach((f) => {
    formData.append(
      "files",
      f.file,
      buildStageFileUploadName(f.documentType, f.file),
    );
  });
  return formData;
};

export type StageActionRequest = {
  action: "APPROVE" | "REJECT" | "QUERY";
  remarks?: string;
};

export const fetchDischarge = async (
  visitId: number,
): Promise<DischargeResponse> => {
  const response = await Axios.get(PATIENT_APIS.DISCHARGE(visitId));
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to load discharge");
};

export const saveDischargeDraft = async (
  visitId: number,
  body: SaveDischargeRequest,
  dischargeId?: number,
): Promise<DischargeResponse> => {
  const response = dischargeId
    ? await Axios.patch(PATIENT_APIS.DISCHARGE_BY_ID(visitId, dischargeId), body)
    : await Axios.post(PATIENT_APIS.DISCHARGE(visitId), body);
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to save discharge draft");
};

export const saveDischarge = async (
  visitId: number,
  data: Omit<SaveDischargeRequest, "documents"> & { saveAsDraft: boolean },
  files: StagedFile[],
): Promise<DischargeResponse> => {
  const payload: SaveDischargeRequest = {
    ...data,
    documents: files.map((f) => ({
      documentType: f.documentType,
      fileName: f.file.name,
    })),
  };
  try {
    const response = await Axios.post(
      PATIENT_APIS.DISCHARGE_SAVE(visitId),
      buildSaveFormData(payload as Record<string, unknown>, files),
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    if (response.status === StatusCodes.OK) {
      return response.data;
    }
    throw new Error("Unable to save discharge");
  } catch (error) {
    const fallback = data.saveAsDraft
      ? "Unable to save discharge draft"
      : "Unable to submit discharge";
    const detail = data.saveAsDraft
      ? getApiErrorMessage(error, fallback)
      : getDischargeSubmitErrorMessage(error, fallback);
    const wrapped = new Error(detail) as Error & {
      response?: { data?: unknown };
    };
    wrapped.response = (error as { response?: { data?: unknown } })?.response;
    throw wrapped;
  }
};

export const submitDischarge = async (
  visitId: number,
  dischargeId: number,
): Promise<DischargeResponse> => {
  try {
    const response = await Axios.post(
      PATIENT_APIS.DISCHARGE_SUBMIT(visitId, dischargeId),
    );
    if (response.status === StatusCodes.OK) {
      return response.data;
    }
    throw new Error("Unable to submit discharge");
  } catch (error) {
    const detail = getDischargeSubmitErrorMessage(error);
    const wrapped = new Error(detail) as Error & {
      response?: { data?: unknown };
    };
    wrapped.response = (error as { response?: { data?: unknown } })?.response;
    throw wrapped;
  }
};

export const uploadDischargeDocument = async (
  visitId: number,
  dischargeId: number,
  documentType: string,
  file: File,
): Promise<DischargeResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await Axios.post(
    `${PATIENT_APIS.DISCHARGE_DOCUMENTS(visitId, dischargeId)}?documentType=${encodeURIComponent(documentType)}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to upload discharge document");
};

export const dischargeAction = async (
  visitId: number,
  dischargeId: number,
  body: StageActionRequest,
): Promise<DischargeResponse> => {
  const response = await Axios.post(
    PATIENT_APIS.DISCHARGE_ACTIONS(visitId, dischargeId),
    body,
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to apply discharge action");
};
